import { createContext, useContext, useState, useCallback, useEffect } from 'react';
import { StripePayment } from '@/components/StripePayment';
import { useAuth } from './AuthContext';
import { PAYMENT_PLANS, PLAN_INDEX_BY_KEY, type PricingPlan } from '@/lib/pricing';

interface PaymentContextType {
  isSubscribed: boolean;
  showPaymentModal: boolean;
  setShowPaymentModal: (show: boolean) => void;
  openPaymentModal: (eventOrPlan?: React.MouseEvent | number | string, planIndexOrKey?: number | string) => void;
  selectedPlan: PricingPlan;
  paymentPlans: PricingPlan[];
  cancelSubscription: () => void;
  isProcessing: boolean;
  paymentError: string | null;
  subscriptionDate: Date | null;
  customerId: string | null;
  subscriptionId: string | null;
}

const PaymentContext = createContext<PaymentContextType | undefined>(undefined);

// PLAYTEST MODE: grants premium without payment. Off unless explicitly enabled
// via VITE_PLAYTEST_MODE=true. Never enable in production.
const PLAYTEST_MODE = import.meta.env.VITE_PLAYTEST_MODE === 'true';

export function PaymentProvider({ children }: { children: React.ReactNode }) {
  const { checkAuth, isSubscribed: authIsSubscribed, isAuthenticated } = useAuth();
  
  // Never trust a client-persisted flag for entitlement: default to unsubscribed
  // and wait for the server's answer via auth.
  const [isSubscribed, setIsSubscribed] = useState(PLAYTEST_MODE);

  // Sync with auth subscription status (the server is the source of truth)
  useEffect(() => {
    if (PLAYTEST_MODE) return;
    if (isAuthenticated) {
      setIsSubscribed(authIsSubscribed);
    }
  }, [isAuthenticated, authIsSubscribed]);
  
  const [customerId, setCustomerId] = useState<string | null>(() => {
    return localStorage.getItem('backr_customer_id');
  });
  
  const [subscriptionId, setSubscriptionId] = useState<string | null>(() => {
    return localStorage.getItem('backr_subscription_id');
  });
  
  const [subscriptionDate, setSubscriptionDate] = useState<Date | null>(() => {
    const saved = localStorage.getItem('backr_subscription_date');
    return saved ? new Date(saved) : null;
  });
  
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [clickPosition, setClickPosition] = useState<{ x: number; y: number } | null>(null);
  const [selectedPlan, setSelectedPlan] = useState<PricingPlan>(PAYMENT_PLANS[1]); // Default to Fundraise Plan
  const [isProcessing] = useState(false);
  const [paymentError] = useState<string | null>(null);

  // Save billing metadata to localStorage when values change. Subscription status
  // itself is deliberately not persisted — it is re-read from the server on auth.
  useEffect(() => {
    if (customerId) localStorage.setItem('backr_customer_id', customerId);
    if (subscriptionId) localStorage.setItem('backr_subscription_id', subscriptionId);
    if (subscriptionDate) localStorage.setItem('backr_subscription_date', subscriptionDate.toISOString());
  }, [customerId, subscriptionId, subscriptionDate]);

  // Open payment modal with click position for magic animation
  const openPaymentModal = useCallback((eventOrPlan?: React.MouseEvent | number | string, planIndexOrKey: number | string = 1) => {
    // Skip if already subscribed AND authenticated (paid customer)
    if (isAuthenticated && (isSubscribed || authIsSubscribed)) {
      return;
    }

    let resolvedIndex = typeof planIndexOrKey === 'number'
      ? planIndexOrKey
      : (PLAN_INDEX_BY_KEY[planIndexOrKey.toLowerCase()] ?? 1);
    let clickEvent: React.MouseEvent | undefined;

    if (typeof eventOrPlan === 'number') {
      resolvedIndex = eventOrPlan;
    } else if (typeof eventOrPlan === 'string') {
      const key = eventOrPlan.toLowerCase();
      resolvedIndex = PLAN_INDEX_BY_KEY[key] ?? resolvedIndex;
    } else if (eventOrPlan) {
      clickEvent = eventOrPlan;
    }

    setSelectedPlan(PAYMENT_PLANS[resolvedIndex] || PAYMENT_PLANS[1]);
    if (clickEvent) {
      setClickPosition({ x: clickEvent.clientX, y: clickEvent.clientY });
    }
    setShowPaymentModal(true);
  }, [isSubscribed, isAuthenticated, authIsSubscribed]);

  // Handle successful payment
  const handlePaymentSuccess = useCallback(async () => {
    setIsSubscribed(true);
    setSubscriptionDate(new Date());
    
    // Refresh auth to get updated approval status (auto-approved via payment)
    await checkAuth();
    
    // Close modal after showing success briefly
    setTimeout(() => {
      setShowPaymentModal(false);
    }, 2000);
  }, [checkAuth]);

  const cancelSubscription = useCallback(() => {
    setIsSubscribed(false);
    setCustomerId(null);
    setSubscriptionId(null);
    setSubscriptionDate(null);
    localStorage.removeItem('backr_customer_id');
    localStorage.removeItem('backr_subscription_id');
    localStorage.removeItem('backr_subscription_date');
  }, []);

  return (
    <PaymentContext.Provider
      value={{
        isSubscribed,
        showPaymentModal,
        setShowPaymentModal,
        openPaymentModal,
        selectedPlan,
        paymentPlans: PAYMENT_PLANS,
        cancelSubscription,
        isProcessing,
        paymentError,
        subscriptionDate,
        customerId,
        subscriptionId,
      }}
    >
      {children}
      
      {/* Stripe Payment Modal */}
      <StripePayment
        open={showPaymentModal}
        onOpenChange={setShowPaymentModal}
        amount={selectedPlan.price}
        planName={selectedPlan.name}
        planDays={selectedPlan.days}
      />
    </PaymentContext.Provider>
  );
}

export function usePayment() {
  const context = useContext(PaymentContext);
  if (context === undefined) {
    throw new Error('usePayment must be used within a PaymentProvider');
  }
  return context;
}
